// src/pages/Appli/AppliAdmin/VisitorCenter/components/VisitorAnalyticsPanel.jsx

import React from "react";

import {
  FiCalendar,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";

import VisitorAnalyticsKpis from "./VisitorAnalyticsKpis";
import VisitorTrafficChart from "./VisitorTrafficChart";
import VisitorTopPagesTable from "./VisitorTopPagesTable";
import VisitorBreakdownCards from "./VisitorBreakdownCards";

import {
  ErrorState,
  LoadingState,
} from "../ui/VisitorCenterUi";

import {
  formatDateOnly,
} from "../utils/visitorFormatters";

const RANGE_OPTIONS = [
  {
    value: "7d",
    label: "7 jours",
  },
  {
    value: "30d",
    label: "30 jours",
  },
  {
    value: "90d",
    label: "90 jours",
  },
  {
    value: "180d",
    label: "6 mois",
  },
  {
    value: "365d",
    label: "1 an",
  },
  {
    value: "custom",
    label: "Personnalisée",
  },
];

export default function VisitorAnalyticsPanel({
  data,
  filters,
  loading,
  errorMessage,

  onRangeChange,
  onFilterChange,
  onRefresh,
}) {
  const hasData =
    Boolean(data);

  const period =
    data?.period || null;

  return (
    <div className="space-y-6">
      <section className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-sm lg:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EEF7FC] text-[#2EA7E0]">
                <FiCalendar />
              </div>

              <div>
                <h2 className="font-extrabold text-[#123B57]">
                  Période analysée
                </h2>

                <p className="text-xs text-slate-500">
                  Comparez automatiquement avec la période précédente.
                </p>
              </div>
            </div>

            {period && (
              <p className="mt-3 text-sm font-semibold text-slate-600">
                Du{" "}
                {formatDateOnly(
                  period.date_from,
                )}{" "}
                au{" "}
                {formatDateOnly(
                  period.date_to,
                )}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {RANGE_OPTIONS.map(
                (option) => {
                  const active =
                    filters.range ===
                    option.value;

                  return (
                    <button
                      key={
                        option.value
                      }
                      type="button"
                      onClick={() =>
                        onRangeChange(
                          option.value,
                        )
                      }
                      className={`rounded-xl px-3 py-2 text-sm font-bold transition ${
                        active
                          ? "bg-[#123B57] text-white shadow-sm"
                          : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                },
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              {filters.range ===
                "custom" && (
                <>
                  <label>
                    <span className="mb-1 block text-xs font-bold uppercase tracking-[0.08em] text-slate-400">
                      Du
                    </span>

                    <input
                      type="date"
                      value={
                        filters.date_from
                      }
                      onChange={(
                        event,
                      ) =>
                        onFilterChange(
                          "date_from",
                          event.target
                            .value,
                        )
                      }
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#2EA7E0]"
                    />
                  </label>

                  <label>
                    <span className="mb-1 block text-xs font-bold uppercase tracking-[0.08em] text-slate-400">
                      Au
                    </span>

                    <input
                      type="date"
                      value={
                        filters.date_to
                      }
                      onChange={(
                        event,
                      ) =>
                        onFilterChange(
                          "date_to",
                          event.target
                            .value,
                        )
                      }
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#2EA7E0]"
                    />
                  </label>
                </>
              )}

              <label className="flex min-h-[42px] items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-600">
                <FiShield className="text-[#2EA7E0]" />

                <input
                  type="checkbox"
                  checked={
                    filters.exclude_bots ===
                    "true"
                  }
                  onChange={(
                    event,
                  ) =>
                    onFilterChange(
                      "exclude_bots",
                      event.target
                        .checked
                        ? "true"
                        : "false",
                    )
                  }
                />

                Exclure les robots
              </label>

              <button
                type="button"
                onClick={() =>
                  onRefresh()
                }
                disabled={loading}
                className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-[#123B57] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#1C567F] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiRefreshCw
                  className={
                    loading
                      ? "animate-spin"
                      : ""
                  }
                />

                Actualiser
              </button>
            </div>
          </div>
        </div>
      </section>

      <ErrorState
        message={errorMessage}
      />

      {loading && !hasData ? (
        <div className="rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <LoadingState
            label="Calcul des statistiques visiteurs..."
            minHeight="min-h-[420px]"
          />
        </div>
      ) : hasData ? (
        <>
          <VisitorAnalyticsKpis
            summary={
              data.summary
            }
            comparison={
              data.comparison
            }
          />

          <VisitorTrafficChart
            rows={
              data.daily_activity ||
              []
            }
          />

          <VisitorTopPagesTable
            rows={
              data.top_pages || []
            }
          />

          <VisitorBreakdownCards
            sources={
              data.sources || []
            }
            devices={
              data.devices || []
            }
            browsers={
              data.browsers || []
            }
          />
        </>
      ) : (
        <div className="rounded-[28px] border border-slate-200 bg-white p-10 text-center shadow-sm">
          <h3 className="font-extrabold text-[#123B57]">
            Aucune statistique disponible
          </h3>

          <p className="mt-2 text-sm text-slate-500">
            Sélectionnez une autre période ou attendez les premières visites.
          </p>
        </div>
      )}
    </div>
  );
}